import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import LogoImage from './LogoImage';
import { firmInfo } from '../mockData';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/contact', label: 'Contact' },
    { path: 'https://forms.gle/oEHDTk1DE5tVRCt8A', label: 'Careers', external: true },
    { path: 'https://forms.gle/1HMiCaaVV3uREu8XA', label: 'Internships', external: true }
  ];

  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 999,
        height: '80px',
        background: isScrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderBottom: isScrolled ? '1px solid rgba(201, 168, 113, 0.2)' : 'none',
        transition: 'all 0.3s ease',
        boxShadow: isScrolled ? '0 2px 8px rgba(0, 0, 0, 0.05)' : 'none'
      }}
    >
      <div className="container" style={{ height: '100%' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '100%'
          }}
        >
          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <LogoImage height="50px" />
          </Link>

          {/* Desktop Navigation */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2.5rem'
            }}
            className="desktop-nav"
          >
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: 'none',
                    color: 'var(--text-primary)',
                    fontWeight: '500',
                    fontSize: '0.95rem',
                    transition: 'color 0.2s ease',
                    paddingBottom: '0.25rem'
                  }}
                  onMouseEnter={(e) => (e.target.style.color = 'var(--accent-gold-dark)')}
                  onMouseLeave={(e) => (e.target.style.color = 'var(--text-primary)')}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  style={{
                    textDecoration: 'none',
                    color: location.pathname === link.path ? 'var(--accent-gold-dark)' : 'var(--text-primary)',
                    fontWeight: location.pathname === link.path ? '600' : '500',
                    fontSize: '0.95rem',
                    transition: 'color 0.2s ease',
                    borderBottom: location.pathname === link.path ? '2px solid var(--accent-gold)' : 'none',
                    paddingBottom: '0.25rem'
                  }}
                  onMouseEnter={(e) => (e.target.style.color = 'var(--accent-gold-dark)')}
                  onMouseLeave={(e) => {
                    if (location.pathname !== link.path) {
                      e.target.style.color = 'var(--text-primary)';
                    }
                  }}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="mobile-menu-toggle"
            style={{
              display: 'none',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              color: 'var(--text-primary)'
            }}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="mobile-menu"
          style={{
            display: 'none',
            position: 'absolute',
            top: '80px',
            left: 0,
            right: 0,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            padding: '1.5rem',
            borderBottom: '1px solid rgba(201, 168, 113, 0.2)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
          }}
        >
          {navLinks.map((link) => (
            link.external ? (
              <a
                key={link.path}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  display: 'block',
                  padding: '1rem',
                  textDecoration: 'none',
                  color: 'var(--text-primary)',
                  fontWeight: '500',
                  fontSize: '1rem',
                  transition: 'all 0.2s ease'
                }}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  display: 'block',
                  padding: '1rem',
                  textDecoration: 'none',
                  color: location.pathname === link.path ? 'var(--accent-gold-dark)' : 'var(--text-primary)',
                  fontWeight: location.pathname === link.path ? '600' : '500',
                  fontSize: '1rem',
                  borderLeft: location.pathname === link.path ? '3px solid var(--accent-gold)' : 'none',
                  paddingLeft: location.pathname === link.path ? '0.85rem' : '1rem',
                  transition: 'all 0.2s ease'
                }}
              >
                {link.label}
              </Link>
            )
          ))}
        </div>
      )}

      <style>
        {`
          @media (max-width: 768px) {
            .desktop-nav {
              display: none !important;
            }
            .mobile-menu-toggle {
              display: block !important;
            }
            .mobile-menu {
              display: block !important;
            }
          }
        `}
      </style>
    </nav>
  );
};

export default Navigation;